import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Gauge,
  Palette,
  Fuel,
  Check,
  X,
  ShieldCheck
} from 'lucide-react';
import { getCarById, createLead } from '../services/firebaseService';
import { extractCarId, carSlug } from '../utils/carSlug';
import { sendCarInterestEmail } from '../services/emailService';
import { setPageMeta } from '../utils/seo';

const CarDetails = () => {
  const { id: slug } = useParams();
  const id = extractCarId(slug);
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    mensagem: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    const fetchCar = async () => {
      try {
        const carData = await getCarById(id);
        if (carData) {
          setCar(carData);
        } else {
          navigate('/');
        }
      } catch (error) {
        console.error('Erro ao buscar carro:', error);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    fetchCar();
  }, [id, navigate]);

  useEffect(() => {
    if (car) {
      const desc = car.descricao || car.observacoes || `${car.marca} ${car.modelo} ${car.ano} disponível na Diamond Cars em Ovar.`;
      setPageMeta({
        title: `${car.marca} ${car.modelo} ${car.ano}`,
        description: desc,
        path: `/carro/${carSlug(car)}`,
        image: car.images?.[0]
      });

      // Schema.org — Car listing
      let schema = document.getElementById('car-schema');
      if (!schema) {
        schema = document.createElement('script');
        schema.id = 'car-schema';
        schema.type = 'application/ld+json';
        document.head.appendChild(schema);
      }
      schema.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Car',
        name: `${car.marca} ${car.modelo}`,
        brand: { '@type': 'Brand', name: car.marca },
        model: car.modelo,
        vehicleModelDate: String(car.ano),
        mileageFromOdometer: { '@type': 'QuantitativeValue', value: car.km, unitCode: 'KMT' },
        fuelType: car.combustivel,
        color: car.cor,
        description: desc,
        image: car.images?.[0],
        offers: {
          '@type': 'Offer',
          price: car.preco,
          priceCurrency: 'EUR',
          availability: car.vendido ? 'https://schema.org/SoldOut' : car.reservado ? 'https://schema.org/LimitedAvailability' : 'https://schema.org/InStock',
          seller: { '@type': 'AutoDealer', name: 'Diamond Cars', url: 'https://www.diamondcars.pt' }
        }
      });
    }
  }, [car]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price || 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Informações do carro para o email
      const carInfo = {
        marca: car.marca,
        modelo: car.modelo,
        ano: car.ano,
        preco: car.preco,
        km: car.km,
        garantiaMeses: car.garantiaMeses || 0
      };

      // Enviar email
      const emailResult = await sendCarInterestEmail(formData, carInfo);

      // Criar lead no sistema
      const leadResult = await createLead({
        ...formData,
        carroId: car._id,
        carroMarca: car.marca,
        carroModelo: car.modelo
      });

      if (emailResult.success || leadResult.success) {
        setSubmitSuccess(true);
        setFormData({ nome: '', email: '', telefone: '', mensagem: '' });
        setTimeout(() => {
          setShowContactForm(false);
          setSubmitSuccess(false);
        }, 3000);
      } else {
        alert('Erro ao enviar mensagem. Por favor tente novamente.');
      }
    } catch (error) {
      console.error('Erro:', error);
      alert('Erro ao enviar mensagem. Por favor tente novamente.');
    }

    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative inline-block">
            <div className="w-16 h-16 border-4 border-blue-200 rounded-full"></div>
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin absolute top-0"></div>
          </div>
          <p className="mt-6 text-gray-600 font-medium">A carregar detalhes...</p>
        </div>
      </div>
    );
  }

  if (!car) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header da Página */}
      <div className="bg-white border-b shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 transition group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-semibold">Voltar às Viaturas</span>
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Coluna Principal */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              {/* Imagem Principal */}
              <div className="relative h-96 md:h-[500px] bg-gray-200">
                {car.images && car.images.length > 0 ? (
                  <img
                    src={car.images[activeImage] || car.images[0]}
                    alt={`${car.marca} ${car.modelo}`}
                    className={`w-full h-full object-cover ${car.vendido || car.reservado ? 'opacity-70' : ''}`}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                    <div className="text-9xl font-bold text-gray-300">
                      {car.marca?.[0]}
                    </div>
                  </div>
                )}
                {/* Setas de navegação */}
                {car.images && car.images.length > 1 && (
                  <>
                    <button
                      onClick={() => setActiveImage(i => (i - 1 + car.images.length) % car.images.length)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-2 transition"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={() => setActiveImage(i => (i + 1) % car.images.length)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-2 transition"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                      {car.images.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setActiveImage(i)}
                          className={`w-2 h-2 rounded-full transition ${i === activeImage ? 'bg-white' : 'bg-white/40'}`}
                        />
                      ))}
                    </div>
                  </>
                )}

                {/* Watermark VENDIDO / RESERVADO */}
                {car.vendido && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="bg-red-600 text-white px-10 py-5 rounded-xl shadow-2xl transform -rotate-12">
                      <span className="text-5xl font-bold tracking-wider">VENDIDO</span>
                    </div>
                  </div>
                )}
                {car.reservado && !car.vendido && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="bg-amber-500 text-white px-10 py-5 rounded-xl shadow-2xl transform -rotate-12">
                      <span className="text-5xl font-bold tracking-wider">RESERVADO</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Galeria de Thumbnails */}
              {car.images && car.images.length > 1 && (
                <div className="p-4 bg-gray-50 border-b overflow-x-auto">
                  <div className="flex gap-2">
                    {car.images.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setActiveImage(index)}
                        className={`relative w-24 h-20 rounded-md overflow-hidden flex-shrink-0 transition-all ${
                          activeImage === index ? 'ring-2 ring-blue-600 ring-offset-2' : 'opacity-60 hover:opacity-100'
                        }`}
                      >
                        <img src={img} alt={`Miniatura ${index + 1}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Informação Principal */}
              <div className="p-8">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8 pb-8 border-b">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                      {car.marca} {car.modelo}
                    </h1>
                    <p className="text-lg text-gray-600">Ano {car.ano}</p>
                  </div>
                  <div className="text-left md:text-right">
                    <div className="text-4xl font-bold text-blue-600 mb-1">
                      {formatPrice(car.preco)}
                    </div>
                    <p className="text-sm text-gray-500">IVA incluído</p>
                  </div>
                </div>

                {/* Descrição */}
                {car.descricao && (
                  <div className="mb-8">
                    <p className="text-xl text-gray-700 font-medium">
                      {car.descricao}
                    </p>
                  </div>
                )}

                {/* Especificações */}
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Especificações
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <Calendar className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 mb-1">Ano</p>
                      <p className="font-bold text-gray-900 text-lg">{car.ano}</p>
                    </div>

                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <Gauge className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 mb-1">Quilómetros</p>
                      <p className="font-bold text-gray-900 text-lg">
                        {car.km?.toLocaleString('pt-PT')} km
                      </p>
                    </div>

                    {car.cor && (
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <Palette className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                        <p className="text-sm text-gray-600 mb-1">Cor</p>
                        <p className="font-bold text-gray-900 text-lg">{car.cor}</p>
                      </div>
                    )}

                    {car.combustivel && (
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <Fuel className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                        <p className="text-sm text-gray-600 mb-1">Combustível</p>
                        <p className="font-bold text-gray-900 text-lg">{car.combustivel}</p>
                      </div>
                    )}
                    
                    {car.garantiaMeses > 0 && (
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <ShieldCheck className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                        <p className="text-sm text-gray-600 mb-1">Garantia</p>
                        <p className="font-bold text-gray-900 text-lg">{car.garantiaMeses} Meses</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Observações */}
                {car.observacoes && (
                  <div className="pt-8 border-t">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      Sobre Esta Viatura
                    </h2>
                    <p className="text-gray-700 leading-relaxed text-lg whitespace-pre-line">
                      {car.observacoes}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar - Contacto */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              {car.vendido ? (
                <div className="bg-red-50 border-2 border-red-200 rounded-lg shadow-md p-6 text-center">
                  <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <X className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-red-800 mb-2">Vendido</h3>
                  <p className="text-red-600">Esta viatura já foi vendida.</p>
                </div>
              ) : car.reservado ? (
                <div className="bg-amber-50 border-2 border-amber-200 rounded-lg shadow-md p-6 text-center">
                  <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">🔒</span>
                  </div>
                  <h3 className="text-2xl font-bold text-amber-800 mb-2">Reservado</h3>
                  <p className="text-amber-700">Esta viatura está reservada.</p>
                </div>
              ) : !showContactForm ? (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Interessado?
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Entre em contacto connosco para mais informações
                  </p>

                  <button
                    onClick={() => setShowContactForm(true)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 px-6 rounded-md font-semibold text-lg transition shadow-md hover:shadow-lg mb-6"
                  >
                    Tenho Interesse
                  </button>

                  <div className="space-y-3 pt-6 border-t">
                    <div className="flex items-center gap-3">
                      <div className="bg-green-100 p-2 rounded-md">
                        <Check className="w-5 h-5 text-green-600" />
                      </div>
                      <span className="text-sm text-gray-700">Resposta em 24h</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="bg-green-100 p-2 rounded-md">
                        <Check className="w-5 h-5 text-green-600" />
                      </div>
                      <span className="text-sm text-gray-700">Sem compromisso</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="bg-green-100 p-2 rounded-md">
                        <Check className="w-5 h-5 text-green-600" />
                      </div>
                      <span className="text-sm text-gray-700">Atendimento personalizado</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">
                      Contacte-nos
                    </h3>
                    <button
                      onClick={() => setShowContactForm(false)}
                      className="p-2 hover:bg-gray-100 rounded-md transition"
                    >
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>

                  {submitSuccess ? (
                    <div className="bg-green-50 border-2 border-green-200 rounded-md p-6 text-center">
                      <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Check className="w-8 h-8 text-green-600" />
                      </div>
                      <h4 className="font-bold text-green-900 text-lg mb-2">Mensagem Enviada!</h4>
                      <p className="text-green-700">
                        Entraremos em contacto em breve
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Nome *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.nome}
                          onChange={(e) =>
                            setFormData({ ...formData, nome: e.target.value })
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                          placeholder="O seu nome"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) =>
                            setFormData({ ...formData, email: e.target.value })
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                          placeholder="seuemail@exemplo.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Telefone *
                        </label>
                        <input
                          type="tel"
                          required
                          value={formData.telefone}
                          onChange={(e) =>
                            setFormData({ ...formData, telefone: e.target.value })
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                          placeholder="+351 XXX XXX XXX"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Mensagem (opcional)
                        </label>
                        <textarea
                          rows="4"
                          value={formData.mensagem}
                          onChange={(e) =>
                            setFormData({ ...formData, mensagem: e.target.value })
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent resize-none"
                          placeholder="Deixe a sua mensagem..."
                        ></textarea>
                      </div>

                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-md font-semibold transition shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {submitting ? 'A enviar...' : 'Enviar Mensagem'}
                      </button>
                    </form>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarDetails;
